/**
 * What the PreMize 3D scene says, in one place: the six layers from the
 * product video's "every layer designed by experts" stack (ฮาร์ดแวร์ →
 * ประสบการณ์ผู้ใช้งาน), and the standout features shown on the floating
 * screens, each a short muted loop cut from the product video by
 * scripts/build-videos.py.
 *
 * The same words render as plain HTML (`PremizeInside`) for crawlers, screen
 * readers and anyone without the 3D, so nothing here exists only in WebGL.
 */

export interface InsideLayer {
  n: string;
  name: string;
  line: string;
}

/** Bottom to top. The first is the box itself. */
export const INSIDE: readonly InsideLayer[] = [
  // The memory size lives in the stats only (no duplicated facts).
  { n: '01', name: 'Hardware', line: 'NVIDIA DGX Spark, in your office' },
  // 3.5x: the product video's own measurement — same machine class, same
  // load, before vs after our tuning. Never against cloud or other GPUs.
  { n: '02', name: 'Serving', line: '3.5x the throughput of the untuned setup, same load' },
  // No model sizes until the owner confirms what ships (HANDOFF owner inputs).
  { n: '03', name: 'Models', line: 'Two models: one for quality, one for speed' },
  // The page citation is the lead's and the first clip's to say.
  { n: '04', name: 'Knowledge', line: 'Your documents, searched and summarized' },
  { n: '05', name: 'Governance', line: 'Policies, permissions and a full audit trail' },
  // LINE is in the M and L packages only.
  { n: '06', name: 'Experience', line: 'Chat, an API, and LINE on M & L packages' },
];

export interface Screen {
  id: string;
  src: string;
  poster: string;
  title: string;
  line: string;
  /** A small print line under the caption: which packages have it, and
   *  whether the clip is time-lapsed (the product video marks those
   *  เร่งเวลา; they must never pass for real-time speed). */
  meta?: string;
}

/** The standout features, chosen from a frame-by-frame review of the product
 *  video (every reviewer ranked the cited answer first). Captions describe
 *  only what the clip shows. Approvals and Agent Studio are tagged M และ L in
 *  the video: M and L packages only. */
export const SCREENS: readonly Screen[] = [
  {
    id: 'cite',
    src: '/video/premize-cite.mp4',
    poster: '/video/premize-cite.webp',
    title: 'Answers with the page',
    line: 'Ask in Thai, click the page number, see the exact line it came from.',
  },
  {
    id: 'ingest',
    src: '/video/premize-ingest.mp4',
    poster: '/video/premize-ingest.webp',
    title: 'Drop in a PDF',
    line: 'It reads it (Thai OCR included), indexes it and writes a summary.',
    meta: 'Clip sped up',
  },
  {
    id: 'approve',
    src: '/video/premize-approve.mp4',
    poster: '/video/premize-approve.webp',
    title: 'Approve from your phone',
    line: 'A quote over your limit waits until the owner taps Approve in chat.',
    meta: 'M & L packages',
  },
  {
    id: 'agents',
    src: '/video/premize-agents.mp4',
    poster: '/video/premize-agents.webp',
    title: 'Agents that pass tests first',
    line: 'Build an agent; it must pass its checks before it goes live.',
    meta: 'M & L packages · clip sped up',
  },
];

export const SCREEN_SIZE = { width: 800, height: 500 } as const;
